import RequiredMark from './RequiredMark';
import React, { InputHTMLAttributes, useId } from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';
import {
    tableToolbarControlInteractiveClass,
    tableToolbarValueTextClass,
    tableToolbarIconClass,
} from '../lib/tableClasses';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
    /** Forwarded to the underlying <input>. Works as a regular prop in React 19. */
    ref?: React.Ref<HTMLInputElement>;
    label?: string | React.ReactNode;
    error?: string;
    /** Red border WITHOUT a message — for proactively flagging a
     *  required-but-empty field (Standards §14.7; the same prop as
     *  `DropdownSelect`). An `error` still wins: it carries the message. */
    invalid?: boolean;
    /** Required field: a red asterisk after the label and `aria-required`
     *  (Standards §14.7). Not `required`, which on an input is the browser's
     *  own validation and blocks the form with its bubble. */
    markRequired?: boolean;
    icon?: IconName | React.ReactElement;
    /** Trailing suffix rendered inside the input (e.g. "€", "kg", "%"). */
    suffix?: string | React.ReactNode;
    size?: 'default' | 'compact';
    labelRight?: React.ReactNode;
    /**
     * Visual preset:
     * - `default`  → form field (border, shadow, h-10)
     * - `tableToolbar` → compact filter bar control inside table headers
     * - `search` → header / dropdown search (icon-friendly, soft bg, rounded-lg)
     * - `inline` → minimal styling (transparent, no border) for editable badges
     *   and table cells; caller sets width + alignment via `className`
     */
    variant?: 'default' | 'tableToolbar' | 'search' | 'inline';
    /**
     * When true, the field sizes to its content (via CSS `field-sizing: content`)
     * with a minimum determined by `className` (e.g. `min-w-[80px]`). Useful for
     * tight numeric inputs that should grow when digits don't fit.
     */
    autoWidth?: boolean;
    /**
     * Background tone — overrides the variant default surface so a stack of
     * sibling fields (Input + DropdownSelect, etc.) inside the same form share
     * a single layer per the layer hierarchy rule (see
     * ``shared/ui/lib/layerClasses.ts``).
     *
     * - `transparent` → inherits parent (current default for `default` variant).
     * - `elevated`    → ``bg-slate-50/50 dark:bg-slate-800/50`` — Tone B,
     *   alternates lighter than a layer-1 modal body. Use this when fields sit
     *   directly on a modal/section background and you want the rule
     *   "darker → lighter → darker" to read cleanly.
     */
    surface?: 'transparent' | 'elevated';
}

/**
 * Reusable Input component with label and error handling.
 * Inherits standard HTML input attributes.
 */

const Input: React.FC<InputProps> = ({
    label,
    error,
    invalid = false,
    markRequired = false,
    icon,
    suffix,
    size = 'default',
    labelRight,
    variant = 'default',
    autoWidth = false,
    surface,
    className = '',
    ref,
    ...props
}) => {
    // A real <label htmlFor> instead of a decorative <span>: without it a
    // screen reader announced 199 fields as "edit text". The caller's own id
    // (from `props`) wins over the generated one.
    const autoId = useId();
    const inputId = props.id ?? autoId;
    const errorId = `${inputId}-error`;
    const surfaceClass =
        surface === 'elevated'
            ? 'bg-slate-50/50 dark:bg-slate-800/50'
            : variant === 'tableToolbar'
                ? 'bg-white dark:bg-surface-dark/60'
                : variant === 'search'
                    ? 'bg-slate-50 dark:bg-surface-dark/80'
                    : 'bg-transparent';

    const paddingLeft = icon ? 'pl-10' : 'pl-3';
    // Tight right padding when suffix is a short 1-2 char string; looser otherwise.
    const paddingRight = suffix
        ? (typeof suffix === 'string' && suffix.length <= 2 ? 'pr-7' : 'pr-10')
        : 'pr-3';
    // Hide native number spinners so they don't eat space next to the suffix.
    const noSpinners =
        '[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0 [-moz-appearance:textfield]';
    const widthClass = autoWidth ? 'w-auto [field-sizing:content]' : 'w-full';

    const toolbarInputClasses = [
        'rounded-md border shadow-sm outline-none disabled:opacity-50 disabled:cursor-not-allowed',
        widthClass,
        surfaceClass,
        'border-border-strong dark:border-border-strong-dark',
        'h-10 py-0',
        paddingLeft,
        paddingRight,
        noSpinners,
        tableToolbarValueTextClass,
        'placeholder:text-slate-500 dark:placeholder:text-slate-400',
        tableToolbarControlInteractiveClass,
        error ? '!border-red-500 focus:!ring-red-500/30 focus:!border-red-500' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const defaultInputClasses = [
        'rounded-md border shadow-sm outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed',
        widthClass,
        surfaceClass,
        'border-border-strong dark:border-border-strong-dark',
        'text-text-main dark:text-white',
        paddingLeft,
        paddingRight,
        noSpinners,
        size === 'compact' ? 'py-1.5' : 'py-2.5',
        'text-sm placeholder-slate-400 dark:placeholder-slate-600',
        'focus:ring-1 focus:ring-primary focus:border-primary',
        // A read-only field must LOOK read-only: it used to be indistinguishable
        // from an editable one until you typed into it and nothing happened.
        'read-only:cursor-default read-only:text-text-sub dark:read-only:text-slate-400 read-only:bg-slate-100/70 dark:read-only:bg-white/5 read-only:focus:ring-0 read-only:focus:border-border-strong dark:read-only:focus:border-border-strong-dark',
        error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
            : invalid
                ? '!border-red-300 dark:!border-red-500/50'
                : 'hover:border-border-strong dark:hover:border-border-strong-dark',
    ].join(' ');

    const searchInputClasses = [
        'block rounded-lg leading-5 transition-all duration-300 shadow-sm sm:text-sm',
        widthClass,
        surfaceClass,
        'border border-border-subtle dark:border-border-subtle-dark',
        'text-text-main dark:text-white placeholder-text-sub',
        paddingLeft,
        paddingRight,
        'py-2',
        'focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20',
        error ? '!border-red-500 focus:!ring-red-500/30 focus:!border-red-500' : '',
    ].filter(Boolean).join(' ');

    // Inline variant: caller controls width, alignment, and spacing via `className`.
    const inlineInputClasses = [
        'bg-transparent outline-none border-0 p-0 text-text-main dark:text-white',
        'focus:ring-0 focus:border-0',
        noSpinners,
        error ? 'text-red-500' : '',
    ].filter(Boolean).join(' ');

    const hasFloatingLabel = variant === 'default' && (label || labelRight);
    // Toolbar / search / inline fields have no visible label: the placeholder
    // is the only name they have, so it becomes the accessible name unless the
    // caller passed an explicit aria-label.
    const ariaLabel =
        props['aria-label'] ??
        (!(hasFloatingLabel && label) && typeof props.placeholder === 'string' ? props.placeholder : undefined);
    const describedBy = [props['aria-describedby'], error ? errorId : null].filter(Boolean).join(' ') || undefined;

    const wrapperWidthClass = autoWidth ? 'w-auto' : 'w-full';
    return (
        <div className={`${wrapperWidthClass} min-w-0 relative ${hasFloatingLabel ? 'pt-2' : ''}`}>
            {hasFloatingLabel && label && (
                // The label truncates when the field is narrow («MANO DE OB…»),
                // so it carries its own text as a tooltip — which needs it to
                // receive the pointer. Clicking it focuses the input anyway
                // (`htmlFor`), so letting it through costs nothing.
                <label
                    htmlFor={inputId}
                    title={typeof label === 'string' ? label : undefined}
                    className="absolute top-0 left-2 px-1 bg-white dark:bg-surface-dark text-[10px] font-bold text-primary uppercase tracking-wider z-10 truncate max-w-[calc(100%-1rem)] cursor-text"
                >
                    {label}
                    {markRequired && <> <RequiredMark /></>}
                </label>
            )}
            {hasFloatingLabel && labelRight && (
                <div className="absolute top-0 right-2 px-1 z-10 flex items-center">
                    {labelRight}
                </div>
            )}
            <div className={`relative ${variant === 'tableToolbar' && icon ? 'group' : ''}`}>
                {icon && (
                    <div
                        className={
                            variant === 'tableToolbar'
                                ? 'absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none group-focus-within:[&_.bp-icon]:!text-primary dark:group-focus-within:[&_.bp-icon]:!text-primary'
                                : 'absolute top-1/2 -translate-y-1/2 left-0 pl-3 flex items-center pointer-events-none text-slate-400'
                        }
                    >
                        {typeof icon === 'string' ? (
                            <Icon name={icon} className={variant === 'tableToolbar'
                                        ? tableToolbarIconClass
                                        : '!text-[20px]'} />
                        ) : (
                            icon
                        )}
                    </div>
                )}
                <input
                    ref={ref}
                    className={`${
                        variant === 'tableToolbar'
                            ? toolbarInputClasses
                            : variant === 'search'
                                ? searchInputClasses
                                : variant === 'inline'
                                    ? inlineInputClasses
                                    : defaultInputClasses
                    } ${className}`}
                    aria-required={markRequired || props['aria-required'] || undefined}
                    {...props}
                    id={inputId}
                    aria-label={ariaLabel}
                    aria-invalid={error || invalid ? true : props['aria-invalid']}
                    aria-describedby={describedBy}
                />
                {suffix && (
                    <div className="absolute top-1/2 -translate-y-1/2 right-0 pr-3 flex items-center pointer-events-none text-slate-500 dark:text-slate-400 text-sm">
                        {typeof suffix === 'string' ? <span>{suffix}</span> : suffix}
                    </div>
                )}
            </div>
            {error && <p id={errorId} role="alert" className="text-red-500 text-xs mt-1 font-medium">{error}</p>}
        </div>
    );
};

export default Input;
